"use client"

import * as React from "react"

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "@ibada/ui"

import { useLocale, useTranslations } from "@/i18n"
import { MobileFormRow, MobileFormSection, useFormatMoneyText } from "@/shared/components/mobile-form"
import { MobileDetailHero, MobileDetailListRow, MobileDetailScreen } from "@/shared/components/mobile-detail-screen"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { moneyToNumberForChartGeometryOnly } from "@ibada/shared-types/finance"
import { AccountingAccountType } from "@/features/finance/model/accounting-reports-types"
import {
  FinanceFundStatus,
  type FinanceFundRecord,
} from "@/features/finance/model/finance-types"
import { formatDate, formatDateTime, formatMoney } from "@/features/finance/lib/finance-utils"
import {
  useAccountingChartQuery,
  useFinanceActivitiesQuery,
} from "@/features/finance/services/finance-query"

type FundDetailsSheetProps = {
  fund: FinanceFundRecord | null
  open: boolean
  onOpenChange: (open: boolean) => void
  canEdit: boolean
  currency: string
  onEdit: (fund: FinanceFundRecord) => void
}

function DetailRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className='grid grid-cols-[minmax(8rem,0.8fr)_minmax(0,1.2fr)] gap-4 py-2'>
      <dt className='text-muted-foreground text-sm'>{label}</dt>
      <dd className={mono ? "text-right font-mono text-sm tabular-nums" : "text-right text-sm font-medium"}>
        {value}
      </dd>
    </div>
  )
}

export function FundDetailsSheet({ fund, open, onOpenChange, canEdit, currency, onEdit }: FundDetailsSheetProps) {
  const t = useTranslations("finance.funds")
  const restrictionAccountsQuery = useAccountingChartQuery(
    open && fund?.isRestricted
      ? {
          workspaceId: fund.workspaceId,
          branchId: fund.branchId,
          scope: fund.scope,
          type: AccountingAccountType.Expense,
        }
      : null,
  )
  const linkedActivitiesQuery = useFinanceActivitiesQuery(
    open && fund && fund.activityCount > 0
      ? { workspaceId: fund.workspaceId, branchId: fund.branchId, fundId: fund.id, limit: 100 }
      : null,
  )

  const allowedAccountLabels = React.useMemo(() => {
    if (!fund) return []
    const accountsById = new Map((restrictionAccountsQuery.data?.items ?? []).map((account) => [account.id, account]))
    return fund.allowedExpenseAccountIds.map((id) => {
      const account = accountsById.get(id)
      if (!account) return id
      const name = account.name
      return account.code ? `${account.code} ${name}` : name
    })
  }, [fund, restrictionAccountsQuery.data?.items])

  const notSet = t("details.notSet")
  const status = fund ? t(`status.${fund.status.toLowerCase()}` as never) : ""
  const isMobile = useIsMobile()
  const locale = useLocale()
  const formatMoneyText = useFormatMoneyText(fund?.currency ?? currency)

  if (isMobile) {
    if (!fund) return null
    // A day and time without the "(UTC+02:00)" suffix: the reader is in their own time zone.
    const when = (value: string) =>
      new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value))
    const day = (value: string | null | undefined) =>
      value ? new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(new Date(value)) : undefined
    const target = fund.targetAmount
    // Geometry only (the bar's width); the figures shown stay the exact strings.
    const percent = target
      ? Math.max(0, Math.round((moneyToNumberForChartGeometryOnly(fund.balance) / Math.max(moneyToNumberForChartGeometryOnly(target), 0.0001)) * 100))
      : 0
    const active = fund.status === FinanceFundStatus.Active
    return (
      <MobileDetailScreen
        open={open}
        onOpenChange={onOpenChange}
        title={fund.name}
        backLabel={t("mobile.back")}
        action={
          canEdit
            ? {
                label: t("actions.edit"),
                disabled: !active,
                onPress: () => {
                  onOpenChange(false)
                  onEdit(fund)
                },
              }
            : undefined
        }
      >
        <MobileDetailHero
          eyebrow={t("fields.balance")}
          value={formatMoneyText(fund.balance)}
          badge={
            <span
              className={
                active
                  ? "rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-extrabold text-primary"
                  : "rounded-full bg-muted px-2.5 py-0.5 text-xs font-extrabold text-muted-foreground"
              }
            >
              {status}
            </span>
          }
          caption={fund.description || undefined}
          footer={
            target ? (
              <div className='flex flex-col gap-1.5 px-2'>
                <div className='h-1.5 w-full overflow-hidden rounded-full bg-muted'>
                  <div className='h-full rounded-full bg-primary' style={{ width: `${Math.min(100, percent)}%` }} />
                </div>
                <span className='text-xs font-semibold text-muted-foreground tabular-nums'>
                  {percent} % · {formatMoneyText(target)}
                </span>
              </div>
            ) : null
          }
        />

        <MobileFormSection title={t("details.financialTitle")}>
          <MobileFormRow label={t("details.reserved")} value={formatMoneyText(fund.reserved)} />
          <MobileFormRow label={t("details.available")} value={formatMoneyText(fund.available)} />
          <MobileFormRow label={t("fields.targetAmount")} value={target ? formatMoneyText(target) : undefined} placeholder={notSet} />
        </MobileFormSection>

        <MobileFormSection title={t("details.configurationTitle")}>
          <MobileFormRow label={t("details.scope")} value={t(`details.scopes.${fund.scope.toLowerCase()}` as never)} />
          <MobileFormRow label={t("fields.startDate")} value={day(fund.startDate)} placeholder={notSet} />
          <MobileFormRow label={t("fields.endDate")} value={day(fund.endDate)} placeholder={notSet} />
          <MobileFormRow label={t("fields.dedicatedActivity")} value={fund.dedicatedActivityName || undefined} placeholder={notSet} />
        </MobileFormSection>

        {fund.activityCount > 0 ? (
          <MobileFormSection title={`${t("fields.linkedActivities")} · ${fund.activityCount}`}>
            {linkedActivitiesQuery.isLoading ? (
              <div className='space-y-2 p-4'>
                <Skeleton className='h-4 w-2/3' />
                <Skeleton className='h-4 w-1/2' />
              </div>
            ) : (
              (linkedActivitiesQuery.data ?? []).map((activity) => <MobileDetailListRow key={activity.id}>{activity.title}</MobileDetailListRow>)
            )}
          </MobileFormSection>
        ) : null}

        <MobileFormSection title={t("restriction.title")}>
          <MobileFormRow label={t("details.restricted")} value={fund.isRestricted ? t("details.yes") : t("details.no")} />
          {fund.isRestricted ? (
            <>
              <MobileFormRow label={t("restriction.startDate")} value={day(fund.restrictionStartDate)} placeholder={notSet} />
              <MobileFormRow label={t("restriction.endDate")} value={day(fund.restrictionEndDate)} placeholder={notSet} />
            </>
          ) : null}
        </MobileFormSection>

        {fund.isRestricted ? (
          <MobileFormSection
            title={t("restriction.allowedCategories")}
            footer={
              !restrictionAccountsQuery.isLoading && allowedAccountLabels.length === 0 ? (
                fund.restrictionStartDate || fund.restrictionEndDate ? (
                  t("details.anyExpenseAccount")
                ) : (
                  <span className='text-destructive'>{t("details.restrictionNotConfigured")}</span>
                )
              ) : undefined
            }
          >
            {restrictionAccountsQuery.isLoading ? (
              <div className='space-y-2 p-4'>
                <Skeleton className='h-4 w-3/4' />
                <Skeleton className='h-4 w-1/2' />
              </div>
            ) : allowedAccountLabels.length > 0 ? (
              allowedAccountLabels.map((label) => <MobileDetailListRow key={label}>{label}</MobileDetailListRow>)
            ) : (
              <MobileFormRow label={t("restriction.allowedCategories")} value={undefined} placeholder={notSet} />
            )}
          </MobileFormSection>
        ) : null}

        <MobileFormSection title={t("details.historyTitle")}>
          <MobileFormRow label={t("details.createdAt")} value={when(fund.createdAt)} />
          <MobileFormRow label={t("details.updatedAt")} value={when(fund.updatedAt)} />
        </MobileFormSection>
      </MobileDetailScreen>
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange} mobileBottomSheet>
      <SheetContent side='right' className='flex w-full flex-col gap-0 overflow-hidden sm:max-w-xl'>
        {fund ? (
          <>
            <SheetHeader>
              <div className='flex items-center gap-2 pr-8'>
                <SheetTitle>{fund.name}</SheetTitle>
                <Badge variant={fund.status === FinanceFundStatus.Active ? "success" : "secondary"}>{status}</Badge>
              </div>
              <SheetDescription>{fund.description || t("details.noDescription")}</SheetDescription>
            </SheetHeader>

            <div className='flex-1 space-y-4 overflow-y-auto px-4 py-4'>
              <Card>
                <CardHeader>
                  <CardTitle>{t("details.financialTitle")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className='divide-border divide-y'>
                    <DetailRow label={t("fields.balance")} value={formatMoney(fund.balance, fund.currency ?? currency)} mono />
                    <DetailRow label={t("details.reserved")} value={formatMoney(fund.reserved, fund.currency ?? currency)} mono />
                    <DetailRow label={t("details.available")} value={formatMoney(fund.available, fund.currency ?? currency)} mono />
                    <DetailRow
                      label={t("fields.targetAmount")}
                      value={fund.targetAmount ? formatMoney(fund.targetAmount, fund.currency ?? currency) : notSet}
                      mono={Boolean(fund.targetAmount)}
                    />
                  </dl>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t("details.configurationTitle")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className='divide-border divide-y'>
                    <DetailRow label={t("details.scope")} value={t(`details.scopes.${fund.scope.toLowerCase()}` as never)} />
                    <DetailRow label={t("fields.startDate")} value={fund.startDate ? formatDate(fund.startDate) : notSet} />
                    <DetailRow label={t("fields.endDate")} value={fund.endDate ? formatDate(fund.endDate) : notSet} />
                    <DetailRow label={t("fields.dedicatedActivity")} value={fund.dedicatedActivityName || notSet} />
                    <DetailRow label={t("fields.linkedActivities")} value={String(fund.activityCount)} />
                  </dl>
                  {fund.activityCount > 0 ? (
                    linkedActivitiesQuery.isLoading ? (
                      <div className='space-y-2 pt-3'>
                        <Skeleton className='h-4 w-2/3' />
                        <Skeleton className='h-4 w-1/2' />
                      </div>
                    ) : (
                      <ul className='text-muted-foreground list-disc space-y-1 pl-5 pt-3 text-sm'>
                        {(linkedActivitiesQuery.data ?? []).map((activity) => <li key={activity.id}>{activity.title}</li>)}
                      </ul>
                    )
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t("restriction.title")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className='divide-border divide-y'>
                    <DetailRow label={t("details.restricted")} value={fund.isRestricted ? t("details.yes") : t("details.no")} />
                    {fund.isRestricted ? (
                      <>
                        <DetailRow
                          label={t("restriction.startDate")}
                          value={fund.restrictionStartDate ? formatDate(fund.restrictionStartDate) : notSet}
                        />
                        <DetailRow
                          label={t("restriction.endDate")}
                          value={fund.restrictionEndDate ? formatDate(fund.restrictionEndDate) : notSet}
                        />
                      </>
                    ) : null}
                  </dl>
                  {fund.isRestricted ? (
                    <div className='border-t pt-3'>
                      <p className='text-muted-foreground mb-2 text-sm'>{t("restriction.allowedCategories")}</p>
                      {restrictionAccountsQuery.isLoading ? (
                        <div className='space-y-2'><Skeleton className='h-4 w-3/4' /><Skeleton className='h-4 w-1/2' /></div>
                      ) : allowedAccountLabels.length > 0 ? (
                        <ul className='list-disc space-y-1 pl-5 text-sm'>
                          {allowedAccountLabels.map((label) => <li key={label}>{label}</li>)}
                        </ul>
                      ) : fund.restrictionStartDate || fund.restrictionEndDate ? (
                        <p className='text-sm font-medium'>{t("details.anyExpenseAccount")}</p>
                      ) : (
                        <p className='text-destructive text-sm font-medium'>{t("details.restrictionNotConfigured")}</p>
                      )}
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t("details.historyTitle")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className='divide-border divide-y'>
                    <DetailRow label={t("details.createdAt")} value={formatDateTime(fund.createdAt)} />
                    <DetailRow label={t("details.updatedAt")} value={formatDateTime(fund.updatedAt)} />
                  </dl>
                </CardContent>
              </Card>
            </div>

            {canEdit ? (
              <SheetFooter>
                <Button
                  disabled={fund.status !== FinanceFundStatus.Active}
                  onClick={() => {
                    onOpenChange(false)
                    onEdit(fund)
                  }}
                >
                  {t("actions.edit")}
                </Button>
              </SheetFooter>
            ) : null}
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
